import { buildApiUrl, tenantApiPrefix } from '../config';
import { getTenantIdFromStorage } from '../utils/onboardingLocalStorage';

/**
 * ROCCO's live sections. Read-only: the worker's loops write everything, the
 * screens only watch. A lead's name and number both arrive in full; the screen
 * keeps the number behind an eye until someone asks for it (ui-kit RevealText).
 * The dialer's own outbound number is the one value that arrives masked.
 *
 * Times are UTC ISO strings ("2026-09-26T09:30:00Z"); show them in the
 * viewer's own time.
 */

export type OwnerKind = 'ai' | 'human';

export interface RoccoLead {
  id: string;
  name: string;
  phone: string | null;
  state: string | null;
  channel: string | null;
  source: 'HubSpot' | 'Warehouse';
  arrived_at: string | null;
  status: 'new' | 'assigned' | 'calling' | 'callback_booked' | 'done' | 'too_old';
  owner: string | null;
  owner_kind: OwnerKind | null;
  attempts: number;
  next_call_at: string | null;
  callback_at: string | null;
  /** The zone the callback was agreed in, e.g. "America/New_York". */
  callback_tz?: string | null;
}

export interface QueueLead extends RoccoLead {
  last_outcome: string | null;
}

export interface Callback extends RoccoLead {
  booked_by: string | null;
  booked_at: string | null;
  booking_call_id: string | null;
}

/** 'ringing' | 'in progress' | 'ended' while live; how it went once settled. */
export type CallStatus =
  | 'ringing' | 'in progress' | 'ended'
  | 'connected' | 'no answer' | 'callback' | 'denied' | 'failed';

export interface CallRow {
  id: string;
  lead_id: string | null;
  lead: string;
  agent: string | null;
  agent_kind: OwnerKind;
  started_at: string;
  duration_secs: number | null;
  status: CallStatus;
  live: boolean;
  outcome: string | null;
  has_transcript: boolean;
}

export interface CallDetail extends CallRow {
  summary: string | null;
  transcript: { role: 'agent' | 'user'; message: string; seconds: number | null }[];
}

export interface CallsByDay {
  day: string;
  calls: number;
  connected: number;
  callbacks: number;
  by_ai: number;
}

export interface StoryStep {
  kind: 'arrived' | 'assigned' | 'call' | 'handed_off' | 'closed';
  at: string | null;
  title: string;
  detail?: string | null;
  actor?: string | null;
  actor_kind?: OwnerKind | null;
  status?: CallStatus;
  duration_secs?: number | null;
  call_id?: string;
  has_transcript?: boolean;
}

export interface LeadStory {
  lead: RoccoLead;
  steps: StoryStep[];
  upcoming: {
    kind: 'callback' | 'retry';
    at: string;
    title: string;
    actor: string | null;
    actor_kind: OwnerKind | null;
  } | null;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('klaris_jwt');
  return {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
  };
}

function requireTenantPrefix(): string {
  const tid = getTenantIdFromStorage();
  if (!tid) throw new Error('Not authenticated');
  return tenantApiPrefix(tid);
}

async function get<T>(path: string, fallbackMessage: string): Promise<T> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/rocco${path}`), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : fallbackMessage);
  }
  return response.json();
}

export const fetchQueue = () =>
  get<{ leads: QueueLead[]; calling_now: number; too_old?: number }>('/queue', 'Could not load the live queue');

export const fetchCallbacks = () =>
  get<{ callbacks: Callback[] }>('/callbacks', 'Could not load scheduled callbacks');

export const fetchCalls = () =>
  get<{ calls: CallRow[]; by_day: CallsByDay[] }>('/calls', 'Could not load conversations');

export const fetchCall = (callId: string) =>
  get<CallDetail>(`/calls/${callId}`, 'Could not load that call');

export const fetchLeadStory = (leadId: string) =>
  get<LeadStory>(`/leads/${leadId}/story`, "Could not load that lead's story");

/**
 * Take a lead out of the Live queue: it is never called again, and its story
 * says who took it out. Refused while it is on a call; the message says so.
 */
export async function discardLead(leadId: string): Promise<void> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/rocco/leads/${leadId}/discard`), {
    method: 'POST',
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not discard the lead');
  }
}

/** One background job's latest mark: when it ran, whether it worked, how much today. */
export interface JobHealth {
  last_run_at: string | null;
  last_ok_at: string | null;
  last_error: string | null;
  handled_today: number;
}

export interface Fact {
  label: string;
  value: string;
}

export interface Integration {
  id: 'hubspot' | 'warehouse' | 'dialer';
  name: string;
  kind?: string | null;
  role: 'Leads in' | 'Warehouse' | 'Calls out';
  connected: boolean;
  connector_id?: string | null;
  connector_name?: string | null;
  since?: string | null;
  job: JobHealth | null;
  error?: string | null;
  facts: Fact[];
  built_on?: string[];
  agents?: { name: string; voice: string | null; found: boolean | null }[];
  calls_today?: number;
  connected_today?: number;
}

export interface DayValue {
  day: string;
  value: number;
}

export interface Spotlight {
  warehouse_error: string | null;
  flow: {
    hubspot: { connected: boolean; job: JobHealth | null };
    warehouse: { name: string | null; leads_held: number | null; leads_today: number };
    agents: { names: string[]; calls_today: number };
    dialer: { number: string | null; carrier: string | null; error: string | null };
    advisors: { count: number | null; callbacks_waiting: number };
  };
  tiles: {
    /** The small charts under each figure: the last 7 days, day by day. */
    leads_called: DayValue[];
    seconds_to_first_call: DayValue[];
    ai_calls: DayValue[];
    connected: DayValue[];
    callbacks: DayValue[];
    /** The headline figures: everything the agent has done so far. */
    totals: {
      leads_called: number;
      calls_made: number;
      contacted: number;
      callbacks_booked: number;
      contact_rate: number | null;
      seconds_to_first_call: number | null;
    };
    waiting: number;
  };
  people_vs_ai: {
    people: { calls: number; connected: number; avg_secs: number | null } | null;
    ai: { calls: number; connected: number; avg_secs: number | null };
  };
  ai_endings: { status: CallStatus; value: number }[];
  busiest: { name: string; kind: OwnerKind; calls: number }[];
}

export const fetchIntegrations = () =>
  get<{ integrations: Integration[] }>('/integrations', 'Could not load integrations');

export const fetchSpotlight = () => get<Spotlight>('/spotlight', 'Could not load Spotlight');

/**
 * Listen for "the live queue changed" until `signal` aborts. Resolves when
 * the stream ends for any reason; the caller decides whether to reconnect.
 *
 * Carries no data: `onChange` should fetch the queue again. `onOpen` fires
 * once the server has accepted the stream.
 */
export async function watchQueue(
  onChange: () => void,
  signal: AbortSignal,
  onOpen?: () => void,
): Promise<void> {
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/rocco/queue/stream`), {
    headers: { ...getAuthHeaders(), Accept: 'text/event-stream' },
    signal,
  });
  if (!response.ok || !response.body) throw new Error(`Live updates unavailable (${response.status})`);
  onOpen?.();
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  for (;;) {
    const { value, done } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    let end = buffer.indexOf('\n\n');
    while (end !== -1) {
      if (buffer.slice(0, end).split('\n').includes('event: changed')) onChange();
      buffer = buffer.slice(end + 2);
      end = buffer.indexOf('\n\n');
    }
  }
}

export type RoccoList = 'queue' | 'callbacks' | 'calls';

/**
 * One of the live lists as Excel — every record, masked as on screen, with
 * times in this browser's time zone. Saves the file; returns its name.
 */
export async function downloadRoccoList(list: RoccoList): Promise<string> {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  const params = new URLSearchParams({ list, tz });
  const response = await fetch(buildApiUrl(`${requireTenantPrefix()}/rocco/export.xlsx?${params.toString()}`), {
    headers: getAuthHeaders(),
  });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const detail = body && typeof body === 'object' ? (body as { detail?: string }).detail : null;
    throw new Error(typeof detail === 'string' ? detail : 'Could not download that list');
  }
  const disposition = response.headers.get('Content-Disposition');
  const filename = disposition?.match(/filename=["']?([^"';]+)["']?/)?.[1] ?? `rocco-${list}.xlsx`;
  const blob = await response.blob();
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
  return filename;
}

/** How ROCCO is set up, read from the settings the calls themselves use. */
export interface RoccoConfiguration {
  rules: { when: string; then: string; detail: string }[];
  agents: {
    name: string;
    voice: string | null;
    voice_style: string | null;
    /** `live` only when the provider really has the agent. */
    status: 'live' | 'missing' | 'unknown';
    opening: string;
    follow_up_opening: string;
  }[];
  agent_facts: { label: string; value: string }[];
  provider_error: string | null;
  finds_out: { key: string; label: string; detail: string }[];
  guardrails: { title: string; detail: string }[];
}

export const fetchConfiguration = () =>
  get<RoccoConfiguration>('/configuration', 'Could not load how the agent is set up');
